import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { ConfirmModal } from './ui';
import Button from './ui/Button';
import { type EventStatus } from './EventStatusBadge';
import { translateError } from '../utils/errorMessages';
import './EventStatusActions.css';

/** What cancelling would do, straight from GET /api/events/:id/cancel-preview. */
interface CancelPreview {
  unpaidCharges: number;
  paidCharges: number;
  entryCreditsToReturn: number;
  affectedMembers: number;
}

export interface EventStatusActionsProps {
  eventId: string;
  eventName?: string;
  status?: EventStatus;
  /** Called after any successful transition so the page can refetch. */
  onChanged: () => void | Promise<void>;
  /** 'icon' for a table cell, 'labelled' for a toolbar. */
  appearance?: 'icon' | 'labelled';
  className?: string;
}

/**
 * Cancel / put back on / mark as on schedule for one occurrence. One component
 * for all three surfaces so the copy and guards cannot drift apart.
 */
// Semaphore: delete is red, cancel amber, and the recoveries (restore, mark as
// on schedule) are plain — they undo a caution rather than raise one.
const EventStatusActions: React.FC<EventStatusActionsProps> = ({
  eventId,
  eventName,
  status,
  onChanged,
  appearance = 'icon',
  className = '',
}) => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { addToast } = useToast();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<CancelPreview | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);

  const canEdit = hasPermission('events:write');
  if (!canEdit) return null;

  const current: EventStatus = status ?? 'scheduled';
  const labelled = appearance === 'labelled';

  const openConfirm = async () => {
    setPreview(null);
    setPreviewFailed(false);
    setConfirmOpen(true);
    try {
      const { data } = await api.events.cancelPreview(eventId);
      setPreview(data as CancelPreview);
    } catch {
      // The numbers are a courtesy; the dialog still states what happens.
      setPreviewFailed(true);
    }
  };

  const run = async (action: () => Promise<unknown>, successKey: string, errorKey: string) => {
    if (loading) return;
    setLoading(true);
    try {
      await action();
      await onChanged();
      addToast(t(successKey), 'success');
    } catch (err: any) {
      addToast(translateError(err, t, errorKey), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirm = () =>
    run(() => api.events.cancel(eventId), 'events.cancelSuccess', 'events.cancelError')
      .finally(() => setConfirmOpen(false));

  const handleRestore = () =>
    run(() => api.events.uncancel(eventId), 'events.restoreSuccess', 'events.restoreError');

  const handleMarkScheduled = () =>
    run(
      () => api.events.update(eventId, { status: 'scheduled' }),
      'events.markScheduledSuccess',
      'events.markScheduledError',
    );

  const suffix = eventName ? ` ${eventName}` : '';

  return (
    <div className={`event-status-actions${labelled ? ' event-status-actions--labelled' : ''} ${className}`.trim()}>
      {current === 'cancelled' ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={loading}
          onClick={(e) => { e.stopPropagation(); void handleRestore(); }}
          aria-label={`${t('events.restoreSession')}${suffix}`}
          title={t('events.restoreSession')}
        >
          <i className="fas fa-rotate-left" aria-hidden="true"></i>
          {labelled && <span>{t('events.restoreSession')}</span>}
        </Button>
      ) : (
        <>
          {current === 'changed' && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={loading}
              onClick={(e) => { e.stopPropagation(); void handleMarkScheduled(); }}
              aria-label={`${t('events.markScheduled')}${suffix}`}
              title={t('events.markScheduled')}
            >
              <i className="fas fa-check" aria-hidden="true"></i>
              {labelled && <span>{t('events.markScheduled')}</span>}
            </Button>
          )}
          <Button
            type="button"
            variant="warning"
            size="sm"
            disabled={loading}
            onClick={(e) => { e.stopPropagation(); void openConfirm(); }}
            aria-label={`${t('events.cancelSession')}${suffix}`}
            title={t('events.cancelSession')}
          >
            <i className="fas fa-ban" aria-hidden="true"></i>
            {labelled && <span>{t('events.cancelSession')}</span>}
          </Button>
        </>
      )}

      <ConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleCancelConfirm}
        title={t('events.cancelTitle')}
        message={
          <div className="event-status-actions__confirm">
            <p>{t('events.confirmCancel')}</p>
            {preview ? (
              <ul className="event-status-actions__preview">
                <li>{t('events.cancelPreview.unpaid', { count: preview.unpaidCharges })}</li>
                <li>{t('events.cancelPreview.paid', { count: preview.paidCharges })}</li>
                <li>{t('events.cancelPreview.entries', { count: preview.entryCreditsToReturn })}</li>
                <li>{t('events.cancelPreview.members', { count: preview.affectedMembers })}</li>
              </ul>
            ) : previewFailed ? null : (
              <ul className="event-status-actions__preview event-status-actions__preview--loading" aria-hidden="true">
                <li /><li /><li /><li />
              </ul>
            )}
          </div>
        }
        confirmText={t('events.cancelSession')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={loading}
      />
    </div>
  );
};

export default EventStatusActions;
